import { useLanguage } from '@/hooks/useLanguage';
import { SupportedLanguage } from '@/i18n';
import { CustomSelect, type SelectOption } from '@/components/ui/CustomSelect';

/**
 * LanguageSwitcher Component
 *
 * UI component for switching application language
 * Supports two variants:
 * - dropdown: Select element (default, compact)
 * - buttons: Button group (more visual)
 *
 * Usage:
 * ```tsx
 * // Dropdown (default)
 * <LanguageSwitcher />
 *
 * // Buttons with native names
 * <LanguageSwitcher variant="buttons" showNativeNames />
 *
 * // Custom styling
 * <LanguageSwitcher className="w-full" />
 * ```
 */

interface LanguageSwitcherProps {
  /**
   * Visual variant of the switcher
   * @default 'dropdown'
   */
  variant?: 'dropdown' | 'buttons';

  /**
   * Show native language names (Deutsch vs German)
   * @default true
   */
  showNativeNames?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export function LanguageSwitcher({
  variant = 'dropdown',
  showNativeNames = true,
  className = '',
}: LanguageSwitcherProps) {
  const { currentLanguage, languages, changeLanguage, isLoading } = useLanguage();

  /**
   * Buttons Variant
   * Visual button group with active state
   */
  if (variant === 'buttons') {
    return (
      <div className={`flex gap-2 ${className}`} role="group" aria-label="Language selection">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            disabled={isLoading || currentLanguage === lang.code}
            className={`
              px-3 py-1.5 text-sm font-medium rounded-md transition-all
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
              ${
                currentLanguage === lang.code
                  ? 'bg-primary-500 text-primary-contrast shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }
              ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            aria-pressed={currentLanguage === lang.code}
            aria-label={`Switch to ${lang.name}`}
          >
            {showNativeNames ? lang.nativeName : lang.name}
          </button>
        ))}
      </div>
    );
  }

  /**
   * Dropdown Variant (Default)
   * Compact select element using CustomSelect
   */
  const options: SelectOption[] = languages.map((lang) => ({
    value: lang.code,
    label: showNativeNames ? lang.nativeName : lang.name,
    description: showNativeNames ? lang.name : undefined, // Show English name as description when showing native names
  }));

  return (
    <CustomSelect
      options={options}
      value={currentLanguage}
      onChange={(value) => changeLanguage(value as SupportedLanguage)}
      disabled={isLoading}
      className={className}
      placeholder="Select language"
    />
  );
}
